import { Button } from '@/shared/components/ui';
import styles from './ButtonsPage.module.css';

export const ButtonsPage = () => {
    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h1>Buttons</h1>
                <p className={styles.description}>
                    Componente Button reutilizable con múltiples variantes, tamaños y estados.
                    Todos los botones siguen el design system de Zafiro ERP.
                </p>
            </div>

            {/* Variantes */}
            <section className={styles.section}>
                <h2>Variantes</h2>
                <p>El componente Button soporta 4 variantes visuales diferentes.</p>

                <div className={styles.showcase}>
                    <div className={styles.example}>
                        <Button variant="primary">Primary Button</Button>
                        <code className={styles.code}>
                            {`<Button variant="primary">Primary Button</Button>`}
                        </code>
                    </div>

                    <div className={styles.example}>
                        <Button variant="secondary">Secondary Button</Button>
                        <code className={styles.code}>
                            {`<Button variant="secondary">Secondary Button</Button>`}
                        </code>
                    </div>

                    <div className={styles.example}>
                        <Button variant="success">Success Button</Button>
                        <code className={styles.code}>
                            {`<Button variant="success">Success Button</Button>`}
                        </code>
                    </div>

                    <div className={styles.example}>
                        <Button variant="danger">Danger Button</Button>
                        <code className={styles.code}>
                            {`<Button variant="danger">Danger Button</Button>`}
                        </code>
                    </div>
                </div>
            </section>

            {/* Tamaños */}
            <section className={styles.section}>
                <h2>Tamaños</h2>
                <p>Disponible en 3 tamaños: Small, Medium y Large.</p>

                <div className={styles.showcase}>
                    <div className={styles.example}>
                        <Button size="sm" variant="primary">Small</Button>
                        <code className={styles.code}>
                            {`<Button size="sm">Small</Button>`}
                        </code>
                    </div>

                    <div className={styles.example}>
                        <Button size="md" variant="primary">Medium</Button>
                        <code className={styles.code}>
                            {`<Button size="md">Medium</Button>`}
                        </code>
                    </div>

                    <div className={styles.example}>
                        <Button size="lg" variant="primary">Large</Button>
                        <code className={styles.code}>
                            {`<Button size="lg">Large</Button>`}
                        </code>
                    </div>
                </div>
            </section>

            {/* Estados */}
            <section className={styles.section}>
                <h2>Estados</h2>
                <p>Los botones pueden estar habilitados o deshabilitados.</p>

                <div className={styles.showcase}>
                    <div className={styles.example}>
                        <Button variant="primary">Enabled</Button>
                        <code className={styles.code}>
                            {`<Button variant="primary">Enabled</Button>`}
                        </code>
                    </div>

                    <div className={styles.example}>
                        <Button variant="primary" disabled>Disabled</Button>
                        <code className={styles.code}>
                            {`<Button variant="primary" disabled>Disabled</Button>`}
                        </code>
                    </div>
                </div>
            </section>

            {/* Full Width */}
            <section className={styles.section}>
                <h2>Full Width</h2>
                <p>Los botones pueden ocupar todo el ancho del contenedor.</p>

                <div className={styles.showcase}>
                    <div className={styles.example} style={{ width: '100%' }}>
                        <Button variant="primary" fullWidth>Full Width Button</Button>
                        <code className={styles.code}>
                            {`<Button variant="primary" fullWidth>Full Width Button</Button>`}
                        </code>
                    </div>
                </div>
            </section>

            {/* Outlined Buttons */}
            <section className={styles.section}>
                <h2>Outlined Buttons</h2>
                <p>Variante outlined con borde y fondo transparente. Ideal para acciones secundarias.</p>

                <div className={styles.showcase}>
                    <div className={styles.example}>
                        <Button variant="primary" outlined>Primary Outlined</Button>
                        <code className={styles.code}>
                            {`<Button variant="primary" outlined>Primary Outlined</Button>`}
                        </code>
                    </div>

                    <div className={styles.example}>
                        <Button variant="secondary" outlined>Secondary Outlined</Button>
                        <code className={styles.code}>
                            {`<Button variant="secondary" outlined>Secondary Outlined</Button>`}
                        </code>
                    </div>

                    <div className={styles.example}>
                        <Button variant="success" outlined>Success Outlined</Button>
                        <code className={styles.code}>
                            {`<Button variant="success" outlined>Success Outlined</Button>`}
                        </code>
                    </div>

                    <div className={styles.example}>
                        <Button variant="danger" outlined>Danger Outlined</Button>
                        <code className={styles.code}>
                            {`<Button variant="danger" outlined>Danger Outlined</Button>`}
                        </code>
                    </div>
                </div>
            </section>

            {/* Rounded Buttons */}
            <section className={styles.section}>
                <h2>Rounded Buttons (Pill Shape)</h2>
                <p>Botones con bordes completamente redondeados (forma de píldora).</p>

                <div className={styles.showcase}>
                    <div className={styles.example}>
                        <Button variant="primary" rounded>Rounded Primary</Button>
                        <code className={styles.code}>
                            {`<Button variant="primary" rounded>Rounded Primary</Button>`}
                        </code>
                    </div>

                    <div className={styles.example}>
                        <Button variant="secondary" rounded>Rounded Secondary</Button>
                        <code className={styles.code}>
                            {`<Button variant="secondary" rounded>Rounded Secondary</Button>`}
                        </code>
                    </div>

                    <div className={styles.example}>
                        <Button variant="primary" outlined rounded>Outlined Rounded</Button>
                        <code className={styles.code}>
                            {`<Button variant="primary" outlined rounded>Outlined Rounded</Button>`}
                        </code>
                    </div>
                </div>
            </section>

            {/* Icon Buttons */}
            <section className={styles.section}>
                <h2>Buttons with Icons</h2>
                <p>Botones con iconos antes o después del texto.</p>

                <div className={styles.showcase}>
                    <div className={styles.example}>
                        <Button
                            variant="primary"
                            iconLeft={<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14" /><path d="m12 5 7 7-7 7" /></svg>}
                        >
                            Icon Left
                        </Button>
                        <code className={styles.code}>
                            {`<Button variant="primary" iconLeft={<ArrowRight />}>
  Icon Left
</Button>`}
                        </code>
                    </div>

                    <div className={styles.example}>
                        <Button
                            variant="success"
                            iconRight={<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>}
                        >
                            Save
                        </Button>
                        <code className={styles.code}>
                            {`<Button variant="success" iconRight={<Check />}>
  Save
</Button>`}
                        </code>
                    </div>

                    <div className={styles.example}>
                        <Button
                            variant="danger"
                            outlined
                            iconLeft={<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18" /><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" /><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" /></svg>}
                        >
                            Delete
                        </Button>
                        <code className={styles.code}>
                            {`<Button variant="danger" outlined iconLeft={<Trash />}>
  Delete
</Button>`}
                        </code>
                    </div>
                </div>
            </section>

            {/* Ghost Buttons */}
            <section className={styles.section}>
                <h2>Ghost Buttons</h2>
                <p>Variante ghost con fondo semi-transparente suave. Ideal para acciones sutiles.</p>

                <div className={styles.showcase}>
                    <div className={styles.example}>
                        <Button variant="primary" ghost>Primary Ghost</Button>
                        <code className={styles.code}>
                            {`<Button variant="primary" ghost>Primary Ghost</Button>`}
                        </code>
                    </div>

                    <div className={styles.example}>
                        <Button variant="secondary" ghost>Secondary Ghost</Button>
                        <code className={styles.code}>
                            {`<Button variant="secondary" ghost>Secondary Ghost</Button>`}
                        </code>
                    </div>

                    <div className={styles.example}>
                        <Button variant="success" ghost>Success Ghost</Button>
                        <code className={styles.code}>
                            {`<Button variant="success" ghost>Success Ghost</Button>`}
                        </code>
                    </div>

                    <div className={styles.example}>
                        <Button variant="danger" ghost>Danger Ghost</Button>
                        <code className={styles.code}>
                            {`<Button variant="danger" ghost>Danger Ghost</Button>`}
                        </code>
                    </div>
                </div>
            </section>

            {/* Icon-Only Buttons */}
            <section className={styles.section}>
                <h2>Icon-Only Buttons</h2>
                <p>Botones compactos solo con icono, sin texto. Perfectos para toolbars y acciones espacialеs.</p>

                <div className={styles.showcase}>
                    <div className={styles.example}>
                        <Button
                            variant="primary"
                            iconOnly={<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>}
                        />
                        <code className={styles.code}>
                            {`<Button variant="primary" iconOnly={<Edit />} />`}
                        </code>
                    </div>

                    <div className={styles.example}>
                        <Button
                            variant="danger"
                            iconOnly={<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18" /><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" /><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" /></svg>}
                        />
                        <code className={styles.code}>
                            {`<Button variant="danger" iconOnly={<Trash />} />`}
                        </code>
                    </div>

                    <div className={styles.example}>
                        <Button
                            variant="primary"
                            outlined
                            rounded
                            iconOnly={<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14" /><path d="M5 12h14" /></svg>}
                        />
                        <code className={styles.code}>
                            {`<Button variant="primary" outlined rounded iconOnly={<Plus />} />`}
                        </code>
                    </div>

                    <div className={styles.example}>
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                            <Button size="sm" variant="secondary" outlined iconOnly={<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>} />
                            <Button size="md" variant="secondary" outlined iconOnly={<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>} />
                            <Button size="lg" variant="secondary" outlined iconOnly={<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>} />
                        </div>
                        <code className={styles.code}>
                            {`// Icon-only buttons en diferentes tamaños
<Button size="sm" iconOnly={<Search />} />
<Button size="md" iconOnly={<Search />} />  
<Button size="lg" iconOnly={<Search />} />`}
                        </code>
                    </div>
                </div>
            </section>

            {/* Loading State */}
            <section className={styles.section}>
                <h2>Loading State</h2>
                <p>Botones con estado de carga que muestran un spinner animado. El botón se deshabilita automáticamente.</p>

                <div className={styles.showcase}>
                    <div className={styles.example}>
                        <Button variant="primary" loading>Guardando...</Button>
                        <code className={styles.code}>
                            {`<Button variant="primary" loading>Guardando...</Button>`}
                        </code>
                    </div>

                    <div className={styles.example}>
                        <Button variant="success" loading>Procesando...</Button>
                        <code className={styles.code}>
                            {`<Button variant="success" loading>Procesando...</Button>`}
                        </code>
                    </div>

                    <div className={styles.example}>
                        <Button variant="danger" outlined loading>Eliminando...</Button>
                        <code className={styles.code}>
                            {`<Button variant="danger" outlined loading>Eliminando...</Button>`}
                        </code>
                    </div>

                    <div className={styles.example}>
                        <Button variant="primary" rounded loading>Cargando...</Button>
                        <code className={styles.code}>
                            {`<Button variant="primary" rounded loading>Cargando...</Button>`}
                        </code>
                    </div>
                </div>
            </section>

            {/* Ejemplos de Uso */}
            <section className={styles.section}>
                <h2>Ejemplos de Uso</h2>

                <div className={styles.exampleGroup}>
                    <h3>Acciones de Formulario</h3>
                    <div className={styles.buttonGroup}>
                        <Button variant="primary">Guardar</Button>
                        <Button variant="secondary">Cancelar</Button>
                    </div>
                    <code className={styles.code}>
                        {`<Button variant="primary">Guardar</Button>
<Button variant="secondary">Cancelar</Button>`}
                    </code>
                </div>

                <div className={styles.exampleGroup}>
                    <h3>Acciones Destructivas</h3>
                    <div className={styles.buttonGroup}>
                        <Button variant="danger">Eliminar</Button>
                        <Button variant="secondary">Cancelar</Button>
                    </div>
                    <code className={styles.code}>
                        {`<Button variant="danger">Eliminar</Button>
<Button variant="secondary">Cancelar</Button>`}
                    </code>
                </div>

                <div className={styles.exampleGroup}>
                    <h3>Confirmación de Éxito</h3>
                    <div className={styles.buttonGroup}>
                        <Button variant="success">✓ Operación Completada</Button>
                    </div>
                    <code className={styles.code}>
                        {`<Button variant="success">✓ Operación Completada</Button>`}
                    </code>
                </div>
            </section>

            {/* Props */}
            <section className={styles.section}>
                <h2>Props</h2>
                <div className={styles.propsTable}>
                    <table>
                        <thead>
                            <tr>
                                <th>Prop</th>
                                <th>Tipo</th>
                                <th>Default</th>
                                <th>Descripción</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><code>variant</code></td>
                                <td><code>'primary' | 'secondary' | 'danger' | 'success'</code></td>
                                <td><code>'primary'</code></td>
                                <td>Variante visual del botón</td>
                            </tr>
                            <tr>
                                <td><code>size</code></td>
                                <td><code>'sm' | 'md' | 'lg'</code></td>
                                <td><code>'md'</code></td>
                                <td>Tamaño del botón</td>
                            </tr>
                            <tr>
                                <td><code>fullWidth</code></td>
                                <td><code>boolean</code></td>
                                <td><code>false</code></td>
                                <td>Si el botón debe ocupar todo el ancho</td>
                            </tr>
                            <tr>
                                <td><code>disabled</code></td>
                                <td><code>boolean</code></td>
                                <td><code>false</code></td>
                                <td>Si el botón está deshabilitado</td>
                            </tr>
                            <tr>
                                <td><code>outlined</code></td>
                                <td><code>boolean</code></td>
                                <td><code>false</code></td>
                                <td>Variante outlined (borde, fondo transparente)</td>
                            </tr>
                            <tr>
                                <td><code>rounded</code></td>
                                <td><code>boolean</code></td>
                                <td><code>false</code></td>
                                <td>Forma de píldora (bordes totalmente redondeados)</td>
                            </tr>
                            <tr>
                                <td><code>iconLeft</code></td>
                                <td><code>ReactNode</code></td>
                                <td>-</td>
                                <td>Icono a mostrar antes del texto</td>
                            </tr>
                            <tr>
                                <td><code>iconRight</code></td>
                                <td><code>ReactNode</code></td>
                                <td>-</td>
                                <td>Icono a mostrar después del texto</td>
                            </tr>
                            <tr>
                                <td><code>ghost</code></td>
                                <td><code>boolean</code></td>
                                <td><code>false</code></td>
                                <td>Variante ghost (fondo semi-transparente suave)</td>
                            </tr>
                            <tr>
                                <td><code>iconOnly</code></td>
                                <td><code>ReactNode</code></td>
                                <td>-</td>
                                <td>Botón compacto solo con icono (sin texto)</td>
                            </tr>
                            <tr>
                                <td><code>loading</code></td>
                                <td><code>boolean</code></td>
                                <td><code>false</code></td>
                                <td>Muestra spinner y deshabilita el botón automáticamente</td>
                            </tr>
                            <tr>
                                <td><code>onClick</code></td>
                                <td><code>() =&gt; void</code></td>
                                <td>-</td>
                                <td>Función a ejecutar al hacer click</td>
                            </tr>
                            <tr>
                                <td><code>...props</code></td>
                                <td><code>ButtonHTMLAttributes</code></td>
                                <td>-</td>
                                <td>Cualquier prop HTML válida para button</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </section>

            {/* Import */}
            <section className={styles.section}>
                <h2>Importación</h2>
                <code className={styles.code}>
                    {`import { Button } from '@/shared/components/ui';`}
                </code>
            </section>
        </div>
    );
};
